import { t } from "i18next"
import Phaser, { GameObjects } from "phaser"
import {
  BERRY_TREE_POSITIONS,
  BOARD_HEIGHT,
  BOARD_WIDTH,
  getRegionTint,
  ItemStats,
  PortalCarouselStages,
  RegionDetails,
  Troopers
} from "../../../../config"
import { getMusicAlt } from "../../../../config/game/music"
import {
  FLOWER_POTS_POSITIONS_BLUE,
  FlowerPotMons
} from "../../../../core/flower-pots"
import type Player from "../../../../models/colyseus-models/player"
import { PokemonAvatarModel } from "../../../../models/colyseus-models/pokemon-avatar"
import { getSynergyTier } from "../../../../models/colyseus-models/synergies"
import PokemonFactory from "../../../../models/pokemon-factory"
import { getPokemonData } from "../../../../models/precomputed/precomputed-pokemon-data"
import { type PVEStage, PVEStages } from "../../../../models/pve-stages"
import type GameState from "../../../../rooms/states/game-state"
import {
  FlowerPots,
  type IPlayer,
  type IPokemon,
  type IPokemonEntity
} from "../../../../types"
import { DungeonMusic } from "../../../../types/enum/Dungeon"
import {
  GameMode,
  GamePhaseState,
  Orientation,
  PokemonActionState,
  PokemonTint,
  Stat,
  Team
} from "../../../../types/enum/Game"
import { Item } from "../../../../types/enum/Item"
import type { PlayerDialog } from "../../../../types/enum/PlayerDialog"
import { Pkm, PkmByIndex } from "../../../../types/enum/Pokemon"
import { SpecialGameRule } from "../../../../types/enum/SpecialGameRule"
import { Synergy } from "../../../../types/enum/Synergy"
import { TownEncounters } from "../../../../types/enum/TownEncounter"
import { Weather } from "../../../../types/enum/Weather"
import type { NonFunctionPropNames } from "../../../../types/HelperTypes"
import { isIn } from "../../../../utils/array"
import { isOnBench } from "../../../../utils/board"
import { logger } from "../../../../utils/logger"
import { randomBetween } from "../../../../utils/random"
import { schemaValues } from "../../../../utils/schemas"
import { hasSpecialGameRuleInState } from "../../../../utils/special-game-rule"
import { GamePokemonDetailDOMWrapper } from "../../pages/component/game/game-pokemon-detail"
import { getGameContainer } from "../../pages/game"
import { playMusic } from "../../pages/utils/audio"
import {
  transformBoardCoordinates,
  transformEntityCoordinates
} from "../../pages/utils/utils"
import { preference } from "../../preferences"
import store from "../../stores"
import { refreshShopUI } from "../../stores/GameStore"
import type AnimationManager from "../animation-manager"
import { PokemonAnimations } from "../components/pokemon-animations"
import { DEPTH } from "../depths"
import type GameScene from "../scenes/game-scene"
import { addAbilitySprite, displayBoost } from "./abilities-animations"
import { BerryTree } from "./berry-tree"
import PokemonSprite from "./pokemon"
import PokemonAvatar from "./pokemon-avatar"
import PokemonSpecial from "./pokemon-special"
import { Portal } from "./portal"
import { TradingPlatform } from "./trading-platform"

export enum BoardMode {
  PICK = "pick",
  BATTLE = "battle",
  TOWN = "town"
}

export default class BoardManager {
  pokemons: Map<string, PokemonSprite>
  uid: string
  scene: GameScene
  state: GameState
  player: Player
  gameMode: GameMode
  mode: BoardMode
  animationManager: AnimationManager
  playerAvatar: PokemonAvatar | null
  opponentAvatar: PokemonAvatar | null
  scoutingAvatars: PokemonAvatar[] = []
  pveChestGroup: Phaser.GameObjects.Group | null
  pveChest: Phaser.GameObjects.Sprite | null
  lightX: number
  lightY: number
  lightCell: Phaser.GameObjects.Sprite | null
  berryTrees: BerryTree[] = []
  flowerPots: Phaser.GameObjects.Sprite[] = []
  flowerPokemonsInPots: PokemonSprite[] = []
  mulchAmountText: Phaser.GameObjects.Text | null = null
  mulchIcon: Phaser.GameObjects.Image | null = null
  groundHoles: Phaser.GameObjects.Sprite[]
  trainingBag: Phaser.GameObjects.Sprite | null = null
  trainingRack: Phaser.GameObjects.Sprite | null = null
  portal: Portal | undefined
  smeargle: PokemonSprite | null = null
  specialGameRule: SpecialGameRule | null = null
  specialGameRule2: SpecialGameRule | null = null
  tradingPlatform: TradingPlatform | null = null

  constructor(
    scene: GameScene,
    player: Player,
    animationManager: AnimationManager,
    uid: string,
    state: GameState
  ) {
    this.pokemons = new Map<string, PokemonSprite>()
    this.uid = uid
    this.scene = scene
    this.state = state
    this.player = player
    this.mode = BoardMode.PICK
    this.animationManager = animationManager
    this.lightX = state.lightX
    this.lightY = state.lightY
    this.gameMode = state.gameMode
    this.specialGameRule = state.specialGameRule
    this.specialGameRule2 = state.specialGameRule2
    this.playerAvatar = null
    this.opponentAvatar = null
    this.lightCell = null
    this.pveChest = null
    this.pveChestGroup = null
    this.groundHoles = []
    this.berryTrees = []
    this.flowerPots = []
    this.flowerPokemonsInPots = []

    if (state.phase == GamePhaseState.FIGHT) {
      this.battleMode(false)
      this.renderBoard(false)
    } else if (state.phase === GamePhaseState.TOWN) {
      this.minigameMode()
      this.renderBoard(false) // render pokemons on bench when loading during a town phase
    } else {
      this.pickMode(false)
    }
  }

  victoryAnimation(winnerId: string) {
    //logger.debug({ winnerId, playerId: this.player.id, opponentId: this.opponentAvatar?.playerId })
    if (winnerId === this.player.id) {
      if (this.playerAvatar) {
        this.animationManager.animatePokemon(
          this.playerAvatar,
          PokemonActionState.HOP,
          false
        )
      }
      if (this.opponentAvatar) {
        this.animationManager.animatePokemon(
          this.opponentAvatar,
          PokemonActionState.HURT,
          false
        )
      }
      if (this.pveChest && this.pveChestGroup) {
        const rewards = [
          ...schemaValues(this.player.pveRewards),
          ...schemaValues(this.player.pveRewardsPropositions)
        ]
        this.openChest(this.pveChestGroup, this.pveChest, rewards)
      }
    } else if (winnerId === this.opponentAvatar?.playerId) {
      this.animationManager.animatePokemon(
        this.opponentAvatar,
        PokemonActionState.HOP,
        false
      )
      this.playerAvatar &&
        this.animationManager.animatePokemon(
          this.playerAvatar,
          PokemonActionState.HURT,
          false
        )
    } else {
      this.playerAvatar &&
        this.animationManager.animatePokemon(
          this.playerAvatar,
          PokemonActionState.IDLE,
          false
        )
      if (this.opponentAvatar) {
        this.animationManager.animatePokemon(
          this.opponentAvatar,
          PokemonActionState.IDLE,
          false
        )
      }
    }
  }

  addPokemonSprite(pokemon: IPokemon): PokemonSprite {
    if (this.pokemons.has(pokemon.id)) {
      return this.pokemons.get(pokemon.id)!
    }
    const coordinates = transformBoardCoordinates(
      pokemon.positionX,
      pokemon.positionY
    )
    const pokemonSprite = new PokemonSprite(
      this.scene,
      coordinates[0],
      coordinates[1],
      pokemon,
      this.player.id,
      false,
      false
    )

    this.animationManager.animatePokemon(pokemonSprite, pokemon.action, false)
    this.pokemons.get(pokemonSprite.id)?.destroy()
    this.pokemons.set(pokemonSprite.id, pokemonSprite)
    this.addLavaBenchPokemonAnimation(pokemonSprite)

    return pokemonSprite
  }

  removePokemon(pokemonToRemove: IPokemon) {
    const pokemonSprite = this.pokemons.get(pokemonToRemove.id)
    if (pokemonSprite) {
      pokemonSprite.destroy()
    }
    this.pokemons.delete(pokemonToRemove.id)
  }

  clearBoard() {
    this.pokemons.forEach((p) => p.destroy())
    this.pokemons.clear()
  }

  renderBoard(phaseJustChanged: boolean) {
    this.clearBoard()

    if (this.mode !== BoardMode.TOWN) {
      this.renderBerryTrees()
      this.renderFlowerPots()
      this.renderGroundHoles()
      this.renderTrainingBag()
    }

    if (this.mode === BoardMode.PICK) {
      this.showLightCell()
    }

    if (
      this.state.gameMode === GameMode.DOUBLE_UP &&
      this.mode !== BoardMode.TOWN
    ) {
      this.renderTradingPlatform()
    }

    if (
      this.mode === BoardMode.BATTLE &&
      hasSpecialGameRuleInState(this.state, SpecialGameRule.BENCH_IS_LAVA)
    ) {
      this.addLavaBenchAnimation()
    }

    this.player.board.forEach((pokemon) => {
      if (this.mode === BoardMode.PICK || isOnBench(pokemon)) {
        this.addPokemonSprite(pokemon)
      }
    })

    if (this.specialGameRule != null || this.specialGameRule2 != null) {
      if (this.smeargle) {
        this.smeargle.destroy()
        this.smeargle = null
      }
      this.addSmeargle()
    }

    if (this.state.stageLevel in PVEStages && this.mode === BoardMode.PICK) {
      this.addPvePokemons(PVEStages[this.state.stageLevel], !phaseJustChanged)
    }
  }

  showLightCell() {
    this.hideLightCell()
    const lightTier = getSynergyTier(this.player.synergies, Synergy.LIGHT)
    if (lightTier > 0) {
      const coordinates = transformBoardCoordinates(this.lightX, this.lightY)
      this.lightCell = this.scene.add.sprite(
        coordinates[0],
        coordinates[1],
        "abilities",
        "LIGHT_CELL/000.png"
      )
      this.lightCell.setDepth(DEPTH.LIGHT_CELL)
      this.lightCell.setScale(2, 2)
      this.lightCell.anims.play("LIGHT_CELL")
    }
  }

  hideLightCell() {
    this.lightCell?.destroy()
    this.lightCell = null
  }

  renderBerryTrees() {
    this.berryTrees.forEach((tree) => tree.destroy())
    this.berryTrees = []
    const grassTier = getSynergyTier(this.player.synergies, Synergy.GRASS)
    const nbTreesPerTier = [0, 1, 2, 3, 3]
    const nbTrees = nbTreesPerTier[grassTier] ?? 0

    for (let i = 0; i < nbTrees; i++) {
      const tree = new BerryTree(
        this,
        BERRY_TREE_POSITIONS[i][0],
        BERRY_TREE_POSITIONS[i][1],
        i
      )
      this.berryTrees.push(tree)
    }
  }

  hideBerryTrees() {
    this.berryTrees.forEach((tree) => tree.destroy())
    this.berryTrees = []
  }

  getNbFlowerPots(): number {
    const floraTier = getSynergyTier(this.player.synergies, Synergy.FLORA)
    const nbPotsPerTier = [0, 1, 2, 3, 4]
    let nbPots = nbPotsPerTier[floraTier] ?? 0
    if (
      floraTier >= 4 &&
      this.player.flowerPots.every((p) => p.evolution === Pkm.DEFAULT)
    ) {
      nbPots = 5
    }
    return nbPots
  }

  renderFlowerPots() {
    this.hideFlowerPots()
    const nbPots = this.getNbFlowerPots()

    for (let i = 0; i < nbPots; i++) {
      const potSprite = this.scene.add.sprite(
        FLOWER_POTS_POSITIONS_BLUE[i][0],
        FLOWER_POTS_POSITIONS_BLUE[i][1],
        "flower_pots",
        FlowerPots[i] + ".png"
      )

      potSprite
        .setDepth(
          i % 2 ? DEPTH.INANIMATE_OBJECTS : DEPTH.INANIMATE_OBJECTS + 0.1
        )
        .setScale(2, 2)
        .setOrigin(0.5, 0.5)
        .setTint(
          getRegionTint(this.scene.mapName, preference("colorblindMode"))
        )
      const potPokemon = this.player.flowerPots[i]

      const simulation = this.scene?.room?.state.simulations.get(
        this.player.simulationId
      )
      const isOnBattle =
        this.mode === BoardMode.BATTLE &&
        simulation?.started &&
        schemaValues(simulation.blueDpsMeter).some(
          (p) => p.id === potPokemon?.id
        )

      if (potPokemon && !isOnBattle) {
        const flowerInPot = new PokemonSprite(
          this.scene,
          FLOWER_POTS_POSITIONS_BLUE[i][0],
          FLOWER_POTS_POSITIONS_BLUE[i][1] - 24,
          potPokemon,
          this.player.id,
          false,
          false
        )
        this.animationManager.animatePokemon(
          flowerInPot,
          PokemonActionState.SLEEP,
          false,
          true
        )
        flowerInPot.draggable = false
        this.flowerPokemonsInPots.push(flowerInPot)
        this.pokemons.set(flowerInPot.id, flowerInPot)
      }

      this.flowerPots.push(potSprite)
    }

    this.updateMulchCount()
  }

  updateMulchCount() {
    const nbPots = this.getNbFlowerPots()
    if (nbPots === 0) {
      this.mulchAmountText?.destroy()
      this.mulchAmountText = null
      this.mulchIcon?.destroy()
      this.mulchIcon = null
      return
    }

    if (this.mulchAmountText === null) {
      this.mulchAmountText = this.displayText(348, 622, "").setOrigin(0, 0)
    }
    this.mulchAmountText.setText(`${this.player.mulch}/${this.player.mulchCap}`)
    if (this.mulchIcon === null) {
      const mulchCollected =
        this.player.items.filter((i) => i === Item.RICH_MULCH).length +
        this.player.flowerPots.reduce((acc, pot) => acc + pot.stars, 0) -
        8
      this.mulchIcon = this.scene.add.image(
        332,
        636,
        "item",
        `${mulchCollected >= 8 ? Item.AMAZE_MULCH : Item.RICH_MULCH}.png`
      )
      this.mulchIcon.setScale(0.25).setDepth(DEPTH.TEXT)
    }
  }

  hideFlowerPots() {
    this.flowerPots.forEach((pot) => pot.destroy())
    this.flowerPokemonsInPots.forEach((p) => {
      p.destroy()
      this.pokemons.delete(p.id)
    })
    this.flowerPots = []
    this.flowerPokemonsInPots = []
    if (this.mulchAmountText) {
      this.mulchAmountText.destroy()
      this.mulchAmountText = null
    }
    if (this.mulchIcon) {
      this.mulchIcon?.destroy()
      this.mulchIcon = null
    }
  }

  renderGroundHoles() {
    this.groundHoles.forEach((hole) => hole.destroy())
    this.groundHoles = []
    for (let row = 0; row < BOARD_HEIGHT / 2; row++) {
      for (let col = 0; col < BOARD_WIDTH; col++) {
        let trenchWidth = 0
        const index = col + row * BOARD_WIDTH
        while (
          col + trenchWidth < BOARD_WIDTH &&
          this.player.groundHoles[index + trenchWidth] === 5
        ) {
          trenchWidth++
        }
        if (trenchWidth >= 2) {
          // trench
          const [x, y] = transformBoardCoordinates(col, row + 1)
          const trench = this.scene.add
            .sprite(x - 44, y + 10, "ground_holes", `trench${trenchWidth}.png`)
            .setOrigin(0, 0.5)
            .setScale(2)
            .setAlpha(0.9)
            .setDepth(DEPTH.BOARD_EFFECT_GROUND_LEVEL)
            .setTint(
              getRegionTint(this.scene.mapName, preference("colorblindMode"))
            )
          this.groundHoles.push(trench)
          col += trenchWidth - 1
        } else {
          // single hole
          const hole = this.player.groundHoles[index]
          if (hole > 0) {
            const [x, y] = transformBoardCoordinates(col, row + 1)
            const groundHole = this.scene.add
              .sprite(x, y + 10, "ground_holes", `hole${hole}.png`)
              .setScale(2)
              .setDepth(DEPTH.BOARD_EFFECT_GROUND_LEVEL)
              .setTint(
                getRegionTint(this.scene.mapName, preference("colorblindMode"))
              )
            this.groundHoles.push(groundHole)
          }
        }
      }
    }
  }

  hideGroundHoles() {
    this.groundHoles.forEach((hole) => hole.destroy())
    this.groundHoles = []
  }

  hideTrainingBag() {
    this.trainingRack?.destroy()
    this.trainingBag?.destroy()
    this.trainingRack = null
    this.trainingBag = null
  }

  renderTrainingBag() {
    this.hideTrainingBag()
    const fightingTier = getSynergyTier(this.player.synergies, Synergy.FIGHTING)
    if (fightingTier >= 4) {
      this.trainingRack = this.scene.add
        .sprite(605, 775, "training_bag", "rack.png")
        .setScale(1.5)
        .setDepth(DEPTH.INANIMATE_OBJECTS)
      this.trainingBag = this.scene.add
        .sprite(621, 750, "training_bag", "bag.png")
        .setScale(1.5)
        .setOrigin(35 / 48, 19 / 72)
        .setDepth(DEPTH.INANIMATE_OBJECTS + 0.1)
    }
  }

  animateTrainingBag() {
    if (!this.trainingBag) return
    this.scene.tweens.add({
      targets: this.trainingBag,
      angle: {
        getStart: () => -10,
        getEnd: () => 10
      },
      ease: "Sine.easeInOut",
      duration: 200,
      yoyo: true,
      repeat: -1
    })
  }

  renderTradingPlatform() {
    this.hideTradingPlatform()
    this.tradingPlatform = new TradingPlatform(this, 1276, 746)
  }

  hideTradingPlatform() {
    this.tradingPlatform?.destroy()
    this.tradingPlatform = null
  }

  displayText(x: number, y: number, label: string, tweenOut: boolean = false) {
    const textStyle = {
      fontSize: "24px",
      fontFamily: "Jost",
      color: "#fff",
      align: "center",
      strokeThickness: 2,
      stroke: "#000"
    }

    const text = this.scene.add.existing(
      new GameObjects.Text(this.scene, x, y, label, textStyle).setOrigin(
        0.5,
        0.5
      )
    )
    text.setDepth(DEPTH.TEXT)

    if (tweenOut) {
      this.scene.add.tween({
        targets: [text],
        ease: "linear",
        duration: 1500,
        delay: 0,
        alpha: {
          getStart: () => 1,
          getEnd: () => 0
        },
        y: {
          getStart: () => y - 50,
          getEnd: () => y - 110
        },
        onComplete: () => {
          text.destroy()
        }
      })
    }

    return text
  }

  updatePlayerAvatar() {
    if (this.playerAvatar) {
      this.playerAvatar.destroy()
    }
    if (this.player.life <= 0) return // do not display avatar when player is dead
    if (this.state.phase === GamePhaseState.TOWN) return // do not display avatar in town since it is on board
    const playerAvatar = new PokemonAvatarModel(
      this.player.id,
      this.player.avatar,
      0,
      0,
      0
    )
    this.playerAvatar = new PokemonAvatar(
      this.scene,
      504,
      696,
      playerAvatar,
      this.player.id
    )
    this.playerAvatar.orientation = Orientation.UPRIGHT
    this.playerAvatar.updateLife(this.player.life)
    this.animationManager.animatePokemon(
      this.playerAvatar,
      this.playerAvatar.action,
      false
    )
  }

  updateOpponentAvatar(
    opponentId: string | null,
    opponentAvatarString: string | null,
    isGhostBattle: boolean = false
  ) {
    if (this.opponentAvatar) {
      this.opponentAvatar.destroy()
      this.opponentAvatar = null
    }
    if (this.pveChestGroup) {
      this.pveChestGroup.destroy(true, true)
      this.pveChest = null
      this.pveChestGroup = null
    }

    if (this.mode === BoardMode.BATTLE && opponentId === "pve") {
      const { chest, chestGroup } = this.addChest(1512, 122)
      this.pveChest = chest
      this.pveChestGroup = chestGroup
    } else if (
      this.mode === BoardMode.BATTLE &&
      opponentAvatarString &&
      opponentId
    ) {
      let opponentLife = 0
      this.state.players.forEach((p) => {
        if (p.id === opponentId) opponentLife = p.life
      })

      // do not display avatar when player is dead
      if (opponentLife <= 0) return

      const opponentAvatar = new PokemonAvatarModel(
        this.player.opponentId,
        opponentAvatarString,
        0,
        0,
        0
      )
      this.opponentAvatar = new PokemonAvatar(
        this.scene,
        1512,
        122,
        opponentAvatar,
        opponentId
      )

      this.opponentAvatar.orientation = Orientation.DOWNLEFT
      this.opponentAvatar.updateLife(opponentLife)
      if (isGhostBattle) this.opponentAvatar.sprite.setAlpha(0.5)
      this.animationManager.animatePokemon(
        this.opponentAvatar,
        this.opponentAvatar.action,
        false
      )

      this.updateScoutingAvatars() // will remove opponent from scouting avatars if needed
    }
  }

  updateScoutingAvatars(resetAll = false) {
    const players = this.state.players
    if (!players) return

    const scoutingPlayers = schemaValues(players).filter((p) => {
      const spectatedPlayer = players.get(p.spectatedPlayerId)

      if (
        !spectatedPlayer ||
        spectatedPlayer.id === p.id || // can't scout yourself
        this.mode === BoardMode.TOWN || // no scouting in town
        p.id === this.opponentAvatar?.playerId // avatar already in opponent box
      )
        return false

      // will show avatar when scouting your board or your fight
      const isSpectatingBoard = spectatedPlayer.id === this.player.id
      const isSpectatingBattle =
        this.mode === BoardMode.BATTLE &&
        spectatedPlayer.simulationId === this.player.simulationId

      return isSpectatingBoard || isSpectatingBattle
    })

    /*logger.debug(
      values(players)
        .map((p) => `${p.name} (${p.id}) is watching ${p.spectatedPlayerId}`)
        .join("\n")
    )
 
    logger.debug(
      "scouting now",
      scoutingPlayers.map((p) => `${p.name} (${p.id})`).join("\n")
    )*/

    this.scoutingAvatars = this.scoutingAvatars.filter((avatar) => {
      // remove player avatars that stopped scouting
      if (
        resetAll ||
        scoutingPlayers.some((p) => p.id === avatar.playerId) === false
      ) {
        avatar.destroy()
        return false
      }
      return true
    })

    const newScoutingAvatars = scoutingPlayers.filter(
      (p) => this.scoutingAvatars.some((a) => a.playerId === p.id) === false
    )
    newScoutingAvatars.forEach((player) => {
      const playerIndex = schemaValues(players).findIndex(
        (p) => p.id === player.id
      )
      const scoutAvatarModel = new PokemonAvatarModel(
        player.id,
        player.avatar,
        0,
        0,
        0
      )

      const scoutAvatar = new PokemonAvatar(
        this.scene,
        1512,
        218 + 48 * playerIndex,
        scoutAvatarModel,
        player.id,
        true
      )

      scoutAvatar.orientation = Orientation.DOWNLEFT
      this.animationManager.animatePokemon(
        scoutAvatar,
        scoutAvatar.action,
        false
      )

      this.scoutingAvatars.push(scoutAvatar)
    })
  }

  updateAvatarLife(playerId: string, value: number) {
    if (
      this.playerAvatar &&
      this.playerAvatar.scene &&
      this.player.id === playerId
    ) {
      this.playerAvatar.updateLife(value)
    }

    if (
      this.opponentAvatar &&
      this.opponentAvatar.scene &&
      this.opponentAvatar.playerId === playerId
    ) {
      this.opponentAvatar.updateLife(value)
    }
  }

  battleMode(phaseJustChanged: boolean) {
    this.mode = BoardMode.BATTLE
    this.hideLightCell()
    this.tradingPlatform?.updateTrade(BoardMode.BATTLE)
    if (!phaseJustChanged) this.removePokemonsOnBoard() // remove immediately board sprites if arriving in battle mode
    this.scene.closeTooltips()
    this.scene.input.setDragState(this.scene.input.activePointer, 0)
    setTimeout(() => {
      const gameState = store.getState().game
      const spectatedPlayer = gameState.players.find(
        (p) => p.id === gameState.playerIdSpectated
      )
      const player = spectatedPlayer ?? this.player
      const simulation = this.scene?.room?.state.simulations.get(
        player.simulationId
      )
      if (spectatedPlayer) {
        const isPVERound = spectatedPlayer.opponentId === "pve"
        const isRedPlayer = gameState.teamSpectated === Team.RED_TEAM
        if (!isPVERound && phaseJustChanged) {
          this.portalTransition(isRedPlayer)
        } else {
          this.updateOpponentAvatar(
            spectatedPlayer.opponentId,
            spectatedPlayer.opponentAvatar,
            simulation?.isGhostBattle
          )
        }
      }
    }, 0) // need to wait for next event loop for state to be up to date

    if (
      phaseJustChanged &&
      hasSpecialGameRuleInState(this.state, SpecialGameRule.BENCH_IS_LAVA)
    ) {
      this.addLavaBenchAnimation()
    }
  }

  removePokemonsOnBoard() {
    this.pokemons.forEach((pkmSprite) => {
      if (
        !isOnBench(pkmSprite) &&
        !(
          FlowerPotMons.includes(PkmByIndex[pkmSprite.pokemon.index]) &&
          pkmSprite.positionY === -1
        )
      ) {
        pkmSprite.destroy()
        this.pokemons.delete(pkmSprite.id)
      }
    })
  }

  pickMode(phaseJustChanged: boolean) {
    this.mode = BoardMode.PICK
    this.scene.setMap(this.player.map)
    if (
      this.scene.cache.audio.has(
        "music_" + RegionDetails[this.player.map].music
      ) &&
      PortalCarouselStages.includes(this.state.stageLevel)
    ) {
      // play back original region music when leaving town
      playMusic(this.scene, RegionDetails[this.player.map].music)
    }
    this.renderBoard(phaseJustChanged)
    this.updatePlayerAvatar()
    this.updateOpponentAvatar(null, null)
    this.updateScoutingAvatars(true)
  }

  minigameMode() {
    this.mode = BoardMode.TOWN
    this.scene.setMap("town")
    if (this.state.townEncounter === TownEncounters.LUDICOLO) {
      playMusic(this.scene, DungeonMusic.CARNIVAL_LUDICOLO)
      this.scene.music?.once("looped", () => {
        playMusic(
          this.scene,
          RegionDetails[this.player.map].music ?? DungeonMusic.TREASURE_TOWN
        )
      })
    } else if (this.state.stageLevel === PortalCarouselStages[0]) {
      playMusic(this.scene, getMusicAlt(DungeonMusic.TREASURE_TOWN_STAGE_0))
    } else if (this.state.stageLevel === PortalCarouselStages[1]) {
      playMusic(this.scene, getMusicAlt(DungeonMusic.TREASURE_TOWN_STAGE_10))
    } else if (this.state.stageLevel === PortalCarouselStages[2]) {
      playMusic(this.scene, getMusicAlt(DungeonMusic.TREASURE_TOWN_STAGE_20))
    }
    this.hideLightCell()
    this.hideBerryTrees()
    this.hideFlowerPots()
    this.hideGroundHoles()
    this.hideTrainingBag()
    this.hideTradingPlatform()
    this.removePokemonsOnBoard()
    this.scene.board?.pokemons.forEach((p) => p.setAlpha(1))
    this.scene.closeTooltips()
    this.scene.input.setDragState(this.scene.input.activePointer, 0)

    if (this.playerAvatar) {
      this.playerAvatar.destroy()
    }
    this.updateOpponentAvatar(null, null)
    this.updateScoutingAvatars(true)
    this.scene.minigameManager?.addVillagers(
      this.scene.room?.state.townEncounter ?? null,
      store.getState().game.podium
    )
  }

  setPlayer(player: Player) {
    if (player.id != this.player.id) {
      this.player = player
      this.renderBoard(false)
      this.updatePlayerAvatar()
      const simulation = this.scene?.room?.state.simulations.get(
        player.simulationId
      )
      this.updateOpponentAvatar(
        this.player.opponentId,
        this.player.opponentAvatar,
        simulation?.isGhostBattle
      )
      this.updateScoutingAvatars(true)
    }
  }

  updatePokemonItems(
    playerId: string,
    pokemon: IPokemon,
    item: Item,
    removed: boolean = false
  ) {
    // logger.debug(change);
    if (this.player.id === playerId) {
      const pkm = this.pokemons.get(pokemon.id)
      if (pkm) {
        pkm.itemsContainer.render(pokemon.items)
      }
      if (item === Item.SHINY_STONE) {
        if (removed) {
          pkm?.removeLight()
        } else {
          pkm?.addLight()
        }
      }
      if (item === Item.BERSERK_GENE) {
        if (removed) {
          pkm?.removeBerserkEffect()
        } else {
          pkm?.addBerserkEffect()
        }
      }
      if (item === Item.AIR_BALLOON) {
        if (removed) {
          pkm?.removeFloatingAnimation()
        } else {
          pkm?.addFloatingAnimation()
        }
      }
    }
  }

  updatePokemonDishes(playerId: string, pokemon: IPokemon, dishes: Item[]) {
    if (this.player.id === playerId) {
      const pokemonUI = this.pokemons.get(pokemon.id)
      if (pokemonUI) {
        pokemonUI.updateDishes(dishes)
      }
    }
  }

  changePokemon<F extends NonFunctionPropNames<IPokemon>>(
    pokemon: IPokemon,
    player: IPlayer,
    field: F,
    value: IPokemon[F],
    previousValue?: IPokemon[F]
  ) {
    const pokemonSprite = this.pokemons.get(pokemon.id)
    let coordinates: number[]
    if (pokemonSprite) {
      switch (field) {
        case "positionX":
        case "positionY": {
          coordinates = transformBoardCoordinates(
            pokemon.positionX,
            pokemon.positionY
          )
          setTimeout(
            () => {
              pokemonSprite.x = coordinates[0]
              pokemonSprite.y = coordinates[1]
            },
            this.scene.spectate ? 3000 : 0 // delay position update for spectators, see https://discord.com/channels/737230355039387749/1489057261593694318
          )
          const simulation = this.scene?.room?.state.simulations.get(
            this.player.simulationId
          )
          if (
            this.mode === BoardMode.BATTLE &&
            !isOnBench(pokemon) &&
            simulation?.started
          ) {
            pokemonSprite.destroy()
            this.pokemons.delete(pokemonSprite.id)
          }
          store.dispatch(refreshShopUI())
          if (!isOnBench(pokemon)) {
            this.showSupportItemsVfx(
              schemaValues(pokemon.items),
              pokemonSprite,
              pokemon.positionX,
              pokemon.positionY
            )
          }

          break
        }

        case "action":
          pokemonSprite.action = value as IPokemon["action"]
          this.animationManager.animatePokemon(
            pokemonSprite,
            value as IPokemon["action"],
            false
          )
          if (
            value === PokemonActionState.TRAINING &&
            pokemon.positionX === 0
          ) {
            this.animateTrainingBag()
          }
          break

        case "hp":
        case "maxHP": {
          const baseHP = getPokemonData(pokemon.name).hp
          const hp = schemaValues(pokemon.items).reduce(
            (acc, item) => acc + (ItemStats[item]?.[Stat.HP] ?? 0),
            pokemon.hp
          )
          const scale = 2 * Math.sqrt(1 + (pokemon.maxHP - baseHP) / baseHP)
          pokemonSprite.sprite.setScale(scale)
          if (previousValue != null && value && value > previousValue) {
            pokemonSprite.displayBoost(Stat.HP)
          }
          if (pokemonSprite.lifebar) {
            pokemonSprite.lifebar.setHp(hp)
          }
          break
        }

        case "atk":
          if (previousValue != null && value && value > previousValue)
            pokemonSprite.displayBoost(Stat.ATK)
          break

        case "def":
          if (previousValue != null && value && value > previousValue)
            pokemonSprite.displayBoost(Stat.DEF)
          break

        case "speed":
          if (previousValue != null && value && value > previousValue)
            pokemonSprite.displayBoost(Stat.SPEED)
          break

        case "ap":
          if (previousValue != null && value && value > previousValue)
            pokemonSprite.displayBoost(Stat.AP)
          if (pokemonSprite.detail instanceof GamePokemonDetailDOMWrapper) {
            pokemonSprite.detail.updatePokemon(pokemonSprite.pokemon)
          }
          break

        case "luck":
          if (previousValue != null && value && value > previousValue)
            pokemonSprite.displayBoost(Stat.LUCK)
          if (pokemonSprite.detail instanceof GamePokemonDetailDOMWrapper) {
            pokemonSprite.detail.updatePokemon(pokemonSprite.pokemon)
          }
          break

        case "shiny":
          this.animationManager.animatePokemon(
            pokemonSprite,
            pokemonSprite.action,
            false
          )
          break

        case "index":
          if (previousValue != null && value !== previousValue) {
            // transformation or evolution mid-fight
            // unload previous index animations
            pokemonSprite.unloadAnimations(
              this.scene,
              previousValue as IPokemonEntity["index"],
              pokemonSprite.pokemon.shiny
                ? PokemonTint.SHINY
                : PokemonTint.NORMAL // previous tint is still used here, this is the one we need to unload
            )
            pokemonSprite.attackSprite =
              PokemonAnimations[PkmByIndex[value as string]]?.attackSprite ??
              pokemonSprite.attackSprite
            // load the new ones
            pokemonSprite.lazyLoadAnimations(this.scene).then(() => {
              pokemonSprite.animationLocked = false
              pokemonSprite.evolutionAnimation()
            })
          }
          break

        case "skill":
          if (previousValue != null && value !== previousValue) {
            pokemonSprite.evolutionAnimation()
          }
          break

        case "supercharged":
          if (value === true && previousValue === false) {
            pokemonSprite.superchargeAnimation(this.scene, false, false)
          }
          break
      }
    }

    this.tradingPlatform?.updateTradeIfPokemonInvolved(
      pokemon,
      player,
      this.mode
    )
  }

  closeTooltips() {
    this.pokemons.forEach((pokemon) => {
      if (pokemon.detail) {
        pokemon.closeDetail()
      }
      if (pokemon.itemsContainer) {
        pokemon.itemsContainer.closeTooltips()
      }
    })
    for (const tree of this.berryTrees.values()) {
      tree.closeDetail()
    }
    this.flowerPokemonsInPots.forEach((pokemon) => {
      if (pokemon.detail) {
        pokemon.closeDetail()
      }
    })
  }

  getBenchSize(): number {
    let benchSize = 0

    this.pokemons.forEach((pokemon) => {
      if (isOnBench(pokemon)) {
        benchSize++
      }
    })

    return benchSize
  }

  showEmote(playerOrPokemonId: string, emote?: string) {
    const avatars = [
      this.playerAvatar,
      this.opponentAvatar,
      ...this.scoutingAvatars
    ]
    const player = avatars.find((a) => a?.playerId === playerOrPokemonId)
    if (player) {
      this.animationManager.play(player, PokemonAnimations[player.name].emote)

      if (emote && emote.startsWith("player_dialog/")) {
        this.scene.board?.displayText(
          player.x,
          player.y - 10,
          t(
            `player_dialog.${emote.substring("player_dialog/".length) as PlayerDialog}`
          ),
          true
        )
      } else if (emote) {
        player.drawSpeechBubble(emote, player === this.opponentAvatar)
      }
    } else {
      const pokemonSprite = this.pokemons.get(playerOrPokemonId)
      if (pokemonSprite) {
        this.animationManager.play(
          pokemonSprite,
          PokemonAnimations[pokemonSprite.name].emote
        )

        if (emote) {
          pokemonSprite.drawSpeechBubble(emote, player === this.opponentAvatar)
        }
      }
    }
  }

  addSmeargle() {
    const rules = [this.specialGameRule, this.specialGameRule2].filter(
      (rule): rule is SpecialGameRule => rule != null
    )
    this.smeargle = new PokemonSpecial({
      scene: this.scene,
      x: 1512,
      y: 396,
      name: Pkm.SMEARGLE,
      orientation: Orientation.DOWNLEFT,
      dialog: rules.map((rule) => t(`scribble_description.${rule}`)).join("\n"),
      dialogTitle: rules.map((rule) => t(`scribble.${rule}`)).join(" & ")
    })
  }

  addPvePokemons(pveStage: PVEStage, immediately: boolean) {
    pveStage.board.forEach(([pkm, boardX, boardY], i) => {
      const [x, y] = transformEntityCoordinates(boardX, boardY - 1, true)
      const id = `pve_${this.state.stageLevel}_${i}`

      const pokemon = PokemonFactory.createPokemonFromName(pkm, {
        shiny: this.state.shinyEncounter
      })
      for (const stat in pveStage.statBoosts) {
        pokemon.applyStat(stat as Stat, pveStage.statBoosts[stat])
      }
      if (
        this.state.townEncounter === TownEncounters.MAROWAK &&
        pveStage.marowakItems &&
        i in pveStage.marowakItems
      ) {
        pveStage.marowakItems[i]!.forEach((item) => pokemon.items.add(item))
      }

      const pkmSprite = new PokemonSprite(
        this.scene,
        x,
        y,
        pokemon,
        id,
        false,
        false
      )

      this.pokemons.set(id, pkmSprite)
      pkmSprite.setDepth(DEPTH.POKEMON)

      if (immediately) {
        pkmSprite.orientation = Orientation.DOWNLEFT
        this.scene.animationManager?.animatePokemon(
          pkmSprite,
          PokemonActionState.IDLE,
          false
        )
      } else {
        pkmSprite.y -= 500
        pkmSprite.orientation = Orientation.DOWN
        pkmSprite.pokemon.action = PokemonActionState.WALK

        this.scene.tweens.add({
          targets: pkmSprite,
          y,
          ease: "Linear",
          duration: 3000,
          onComplete: () => {
            if (pkmSprite) {
              pkmSprite.orientation = Orientation.DOWNLEFT
              this.scene.animationManager?.animatePokemon(
                pkmSprite,
                PokemonActionState.IDLE,
                false
              )
            }
          }
        })
      }
    })
  }

  addPortal() {
    if (this.portal) this.portal.destroy()
    const [x, y] = transformBoardCoordinates(3.5, 5)
    this.portal = new Portal(this.scene, "portal", x, y).setScale(0)
    this.scene.tweens.add({
      targets: this.portal,
      scale: 1.5,
      duration: 5000,
      ease: Phaser.Math.Easing.Sine.Out
    })
  }

  portalTransition(isRedPlayer: boolean) {
    const [portalX, portalY] = transformBoardCoordinates(3.5, 5)
    const opponent = schemaValues(this.state.players).find(
      (p) => p.id === this.player.opponentId
    )
    if (!opponent) {
      logger.error("No opponent found for portal transition")
      return
    }

    if (isRedPlayer) {
      // avatar goes first in the portal
      if (this.playerAvatar != null) {
        this.scene.tweens.add({
          targets: this.playerAvatar,
          ease: Phaser.Math.Easing.Quadratic.In,
          duration: 700,
          scale: 0,
          x: portalX,
          y: portalY
        })
      }

      // move board pokemons into the portal
      const pokemonsToTeleport = [...this.pokemons.values()].filter(
        (p) => FlowerPotMons.includes(PkmByIndex[p.pokemon.index]) === false
      )
      for (const pokemon of pokemonsToTeleport) {
        const delay = randomBetween(0, 300)
        this.scene.tweens.add({
          targets: pokemon,
          ease: Phaser.Math.Easing.Quadratic.In,
          delay,
          duration: 700,
          scale: 0,
          x: portalX,
          y: portalY
        })
      }

      // portal close
      this.scene.tweens.add({
        targets: this.portal,
        ease: Phaser.Math.Easing.Quadratic.In,
        delay: 700,
        duration: 300,
        scale: 0,
        onComplete: () => {
          // switch to opponent map

          this.scene.setMap(opponent.map)

          const simulation = this.scene?.room?.state.simulations.get(
            this.player.simulationId
          )
          if (simulation && simulation.weather === Weather.DROUGHT) {
            // postFX on tilemap layers, needs to be reapplied again if changing map
            getGameContainer().handleWeatherChange(
              simulation,
              simulation.weather
            )
          }

          // move portal to the other side when spawning
          const [x, y] = transformBoardCoordinates(3.5, 2)
          this.portal?.setPosition(x, y).setScale(1)

          // show the opponent pokemons
          opponent.board.forEach((pokemon) => {
            if (simulation?.started || isOnBench(pokemon)) return
            const [x, y] = transformEntityCoordinates(
              pokemon.positionX,
              pokemon.positionY - 1,
              true
            )
            const pokemonSprite = new PokemonSprite(
              this.scene,
              x,
              y,
              pokemon,
              this.player.opponentId,
              false,
              false
            )
            this.animationManager.animatePokemon(
              pokemonSprite,
              PokemonActionState.IDLE,
              false
            )
            this.pokemons.get(pokemonSprite.id)?.destroy()
            this.pokemons.set(pokemonSprite.id, pokemonSprite)
          })

          // show the opponent avatar
          const isGhostOpponent = simulation?.isGhostBattle && !isRedPlayer
          this.updateOpponentAvatar(
            opponent.id,
            opponent.avatar,
            isGhostOpponent
          )

          // replace the red pokemon avatar
          if (this.playerAvatar) {
            this.playerAvatar.x = x
            this.playerAvatar.y = y
            this.scene.tweens.add({
              targets: this.playerAvatar,
              ease: Phaser.Math.Easing.Quadratic.Out,
              duration: 1000,
              scale: 1,
              x: 504,
              y: 696,
              onStart: () => {
                if (this.playerAvatar) {
                  this.animationManager.animatePokemon(
                    this.playerAvatar,
                    PokemonActionState.HOP,
                    false,
                    false
                  )
                }
              }
            })
          }

          // replace the red pokemons
          pokemonsToTeleport.forEach((pokemon) => {
            const [originalX, originalY] = transformBoardCoordinates(
              pokemon.positionX,
              pokemon.positionY
            )
            pokemon.x = x
            pokemon.y = y
            const delay = randomBetween(0, 300)
            this.scene.tweens.add({
              targets: pokemon,
              ease: Phaser.Math.Easing.Quadratic.Out,
              delay,
              duration: 700,
              scale: 1,
              x: originalX,
              y: originalY,
              onStart: () => {
                this.animationManager.animatePokemon(
                  pokemon,
                  PokemonActionState.HOP,
                  false,
                  false
                )
              }
            })
          })

          // close the other side portal
          this.scene.tweens.add({
            targets: this.portal,
            ease: Phaser.Math.Easing.Cubic.In,
            delay: 700,
            duration: 300,
            scale: 0,
            onComplete: () => {
              this.portal?.destroy()
              this.portal = undefined
            }
          })
        }
      })
    } else {
      // opponent avatar move out of the portal
      const simulation = this.scene?.room?.state.simulations.get(
        this.player.simulationId
      )
      const isGhostOpponent = simulation?.isGhostBattle && !isRedPlayer
      this.updateOpponentAvatar(opponent.id, opponent.avatar, isGhostOpponent)
      if (this.opponentAvatar) {
        this.opponentAvatar.x = portalX
        this.opponentAvatar.y = portalY
        this.opponentAvatar.setScale(0)
        this.scene.tweens.add({
          targets: this.opponentAvatar,
          ease: Phaser.Math.Easing.Quadratic.Out,
          duration: 1500,
          scale: 1,
          x: 1512,
          y: 122,
          onStart: () => {
            if (this.opponentAvatar) {
              this.animationManager.animatePokemon(
                this.opponentAvatar,
                PokemonActionState.HOP,
                false,
                false
              )
            }
          }
        })
      }

      // opponent pokemons move out of the portal
      setTimeout(() => {
        const opponent = schemaValues(this.state.players).find(
          (p) => p.id === this.player.opponentId
        )
        if (!opponent) return
        opponent.board.forEach((pokemon) => {
          if (simulation?.started || isOnBench(pokemon)) return
          const pokemonSprite = new PokemonSprite(
            this.scene,
            portalX,
            portalY,
            pokemon,
            this.player.opponentId,
            false,
            false
          )
          pokemonSprite.setScale(0)
          this.pokemons.get(pokemonSprite.id)?.destroy()
          this.pokemons.set(pokemonSprite.id, pokemonSprite)

          const [originalX, originalY] = transformEntityCoordinates(
            pokemon.positionX,
            pokemon.positionY - 1,
            true
          )
          const delay = randomBetween(0, 300)
          this.scene.tweens.add({
            targets: pokemonSprite,
            ease: Phaser.Math.Easing.Quadratic.Out,
            delay,
            duration: 700,
            scale: 1,
            x: originalX,
            y: originalY,
            onStart: () => {
              this.animationManager.animatePokemon(
                pokemonSprite,
                PokemonActionState.HOP,
                false,
                false
              )
            }
          })
        })
      }, 1000)

      // close portal
      this.scene.tweens.add({
        targets: this.portal,
        ease: Phaser.Math.Easing.Cubic.In,
        delay: 1700,
        duration: 300,
        scale: 0,
        onComplete: () => {
          this.portal?.destroy()
          this.portal = undefined
        }
      })
    }
  }

  addChest(x: number, y: number) {
    const chestGroup = this.scene.add.group()
    const chest = this.scene.add
      .sprite(x, y, "chest", "001.png")
      .setScale(1.5)
      .setTint(getRegionTint(this.scene.mapName, preference("colorblindMode")))
    chestGroup.add(chest)
    chestGroup.setDepth(DEPTH.INANIMATE_OBJECTS)
    return { chest, chestGroup }
  }

  openChest(
    chestGroup: Phaser.GameObjects.Group,
    chest: Phaser.GameObjects.Sprite,
    rewards: Item[]
  ) {
    chest.anims.play("open_chest")
    this.showRewards(chestGroup, rewards, chest.x, chest.y, chest.depth + 1)
  }

  showRewards(
    group: Phaser.GameObjects.Group,
    rewards: Item[],
    x: number,
    y: number,
    depth: number
  ) {
    rewards.forEach((item, i) => {
      const itemSprite = this.scene.add.sprite(x, y, "item", item + ".png")
      itemSprite.setScale(0.5).setDepth(depth + 1)
      const shinyEffect = this.scene.add.sprite(x, y, "shine")
      shinyEffect
        .setScale(2)
        .setDepth(depth + 1)
        .play("shine")
      group?.addMultiple([itemSprite, shinyEffect])
      this.scene.tweens.add({
        targets: [itemSprite, shinyEffect],
        ease: Phaser.Math.Easing.Quadratic.Out,
        duration: 1000,
        y: y - 48,
        x: x + (i - (rewards.length - 1) / 2) * 70
      })
    })
  }

  showSupportItemsVfx(
    items: Item[],
    pokemonSprite: PokemonSprite,
    positionX: number,
    positionY: number
  ) {
    const adjacentEffectsItems = [
      Item.ABILITY_SHIELD,
      Item.GRACIDEA_FLOWER,
      Item.EFFICIENT_BANDANNA
    ]

    const shouldDisplayLeft = positionX > 0
    const shouldDisplayRight = positionX < BOARD_WIDTH - 1

    adjacentEffectsItems.forEach((item) => {
      if (items.includes(item)) {
        let statBoost: Stat | null = null
        switch (item) {
          case Item.ABILITY_SHIELD:
            statBoost = Stat.SHIELD
            break
          case Item.GRACIDEA_FLOWER:
            statBoost = Stat.SPEED
            break
          case Item.EFFICIENT_BANDANNA:
            statBoost = Stat.PP
            break
        }
        if (statBoost) {
          displayBoost(pokemonSprite, statBoost, 0, 0)
          if (shouldDisplayLeft) displayBoost(pokemonSprite, statBoost, -1, 0)
          if (shouldDisplayRight) displayBoost(pokemonSprite, statBoost, +1, 0)
        }
      }
    })
  }

  addLavaBenchAnimation() {
    for (let x = 0; x < BOARD_WIDTH; x++) {
      addAbilitySprite(
        this.scene,
        "LAVA",
        0,
        transformBoardCoordinates(x, -0.75),
        {
          animOptions: { repeat: 30 },
          depth: DEPTH.ABILITY_GROUND_LEVEL,
          scale: 2
        }
      )
    }
    this.pokemons.forEach((sprite) => this.addLavaBenchPokemonAnimation(sprite))
  }

  addLavaBenchPokemonAnimation(pokemonSprite: PokemonSprite) {
    if (
      this.mode === BoardMode.BATTLE &&
      hasSpecialGameRuleInState(this.state, SpecialGameRule.BENCH_IS_LAVA) &&
      isOnBench(pokemonSprite.pokemon) &&
      !isIn(Troopers, pokemonSprite.pokemon.name)
    ) {
      pokemonSprite.setLifeBar(pokemonSprite.pokemon, this.scene)
      addAbilitySprite(
        this.scene,
        "BURN_UP",
        0,
        [pokemonSprite.x, pokemonSprite.y],
        {
          animOptions: { repeat: 48 },
          scale: 2
        }
      )
    }
  }
}
